import React, { FC, useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, generatePath } from 'react-router-dom';
import { Formik, Form } from 'formik';

import dayjs from 'utils/extendedDayJS';
import { Session } from 'types/event.types';
import { PRETTY_DATE_FORMAT, PRETTY_TIME_FORMAT } from 'constants/dates';
import { EVENT_EDITOR_V2 } from 'constants/featureFlags';
import { DASHBOARD_EVENT_EDITOR_REGISTRATION_ROUTE } from 'constants/routes';
import useFeatureFlag from 'hooks/useFeatureFlag';
import FormikInputTyped from 'components/common/inputs/FormikInput/FormikInputTyped';
import NewDropdown from 'components/common/controls/Dropdown/NewDropdown';
import { FEEDBACK_GUEST_LIST, FEEDBACK_STATUS } from 'constants/enums';
import { createIdsToStatus } from 'components/dashboard/EditEvent/utils/editEventUtils';
import Modal from 'components/common/modal/Modal';
import AlertBanner from 'components/common/other/AlertBanner';
import InfoIcon from 'components/common/icons/InfoIcon';
import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import {
  UpdateEstimatedAttendanceResponse,
  UpdateEstimatedAttendanceVariables,
  UPDATE_ESTIMATED_ATTENDANCE
} from 'graphql/mutations/guests/event_guests';
import { COMBINED_CHECKIN_PAGE_EVENT_QUERY } from 'graphql/queries/events/event';
import { DARK_BLACK } from 'constants/colours';

interface EstimatedAttendanceModalProps {
  sessionOrEventId: number;
  parentEventId?: string;
  isUsingEstimatedAttendees?: boolean;
  hasUnlimitedSpots?: boolean;
  timezoneCode?: string;
  isEstimatedAttendanceModalOpen: boolean;
  setIsEstimatedAttendanceModalOpen?: any;
  estimatedAttendeesCount?: number;
  isEventMultiSession?: boolean;
  refetchEvent?: () => void;
  isThirtyDaysAfterEventEnded?: boolean;
}

const ACTION_TYPE = Object.freeze({
  UPDATE: 'UPDATE',
  REMOVE: 'REMOVE'
});

const EstimatedAttendanceModal: FC<React.PropsWithChildren<EstimatedAttendanceModalProps>> = ({
  sessionOrEventId,
  parentEventId,
  isEventMultiSession,
  isUsingEstimatedAttendees,
  estimatedAttendeesCount,
  hasUnlimitedSpots,
  timezoneCode,
  isEstimatedAttendanceModalOpen,
  setIsEstimatedAttendanceModalOpen,
  isThirtyDaysAfterEventEnded,
  refetchEvent
}) => {
  const navigate = useNavigate();
  const [isRemoveEstimatedAttendanceModalOpen, setIsRemoveEstimatedAttendanceModalOpen] =
    useState(false);
  const [estimatedAttendees, setEstimatedAttendees] = useState<any>(estimatedAttendeesCount);
  const [selectedGuestStatuses, setSelectedGuestStatuses] = useState<any>([]);
  const [feedbackTime, setFeedbackTime] = useState<string>('');
  const [feedbackStatus, setFeedbackStatus] = useState('');
  const [requestId, setRequestId] = useState<number | null>(null);
  const [showSelectedGuestsBanner, setShowSelectedGuestsBanner] = useState(false);
  const [isParentEventFeedbackEnable, setIsParentEventFeedbackEnable] = useState(false);
  const [sessionHasAttendedGuest, setSessionHasAttendedGuest] = useState(false);
  const [sessionTitle, setSessionTitle] = useState();

  const isEventEditorV2Enabled = useFeatureFlag(EVENT_EDITOR_V2);

  const { loading } = useQuery(COMBINED_CHECKIN_PAGE_EVENT_QUERY, {
    variables: { eventId: parentEventId, parentId: parentEventId, sessionId: sessionOrEventId },
    fetchPolicy: 'network-only',
    onError: (err) => console.error(err),
    onCompleted: (data) => {
      const { parentEvent, latestFeedbackRequest, eventSession } = data;

      setIsParentEventFeedbackEnable(parentEvent.feedbackEnable);
      setSessionHasAttendedGuest(
        parentEvent.sessions.some((session: Session) => session.hasAttendedGuest === true)
      );

      if (latestFeedbackRequest) {
        const {
          sendAt,
          sentAt,
          status,
          selectedGuestStatuses: feedbackGuestStatuses,
          id
        } = latestFeedbackRequest;

        if (feedbackGuestStatuses.length > 0 && isEventMultiSession) {
          setShowSelectedGuestsBanner(true);
        }

        setFeedbackTime(
          dayjs.formatPrettyDateTime({
            dateTime: status === FEEDBACK_STATUS.SCHEDULED ? sendAt : sentAt,
            timezoneCode,
            customFormat: `${PRETTY_DATE_FORMAT}, ${PRETTY_TIME_FORMAT}`
          })
        );
        setFeedbackStatus(status);
        setSelectedGuestStatuses(feedbackGuestStatuses || []);
        setRequestId(id);
      }

      if (
        !isUsingEstimatedAttendees &&
        parentEvent.feedbackEnable &&
        (!latestFeedbackRequest || latestFeedbackRequest.selectedGuestStatuses.length === 0) &&
        !parentEvent.sessions.some((session: Session) => session.hasAttendedGuest === true)
      ) {
        setSelectedGuestStatuses([FEEDBACK_GUEST_LIST.Going]);
      }

      if (isEventMultiSession && eventSession) {
        setSessionTitle(eventSession.title);
      }
    }
  });

  const [updateEstimatedAttendance] = useMutation<
    UpdateEstimatedAttendanceResponse,
    UpdateEstimatedAttendanceVariables
  >(UPDATE_ESTIMATED_ATTENDANCE, {
    refetchQueries: [
      {
        query: COMBINED_CHECKIN_PAGE_EVENT_QUERY,
        variables: { eventId: parentEventId, parentId: parentEventId }
      }
    ],
    onError: (err) => {
      console.error(err);
    }
  });

  const handleAttendanceChange = ({ type, eventId }: any): void => {
    const idsToStatus = createIdsToStatus(FEEDBACK_GUEST_LIST);
    const variables = {
      eventId,
      requestId,
      estimatedAttendees: type === ACTION_TYPE.REMOVE ? null : estimatedAttendees,
      selectedGuestStatuses:
        type === ACTION_TYPE.REMOVE
          ? []
          : selectedGuestStatuses.map((statusId: number) => idsToStatus[statusId])
    };

    updateEstimatedAttendance({
      variables
    })
      .then(() => {
        setIsRemoveEstimatedAttendanceModalOpen(false);
        setIsEstimatedAttendanceModalOpen(false);

        if (refetchEvent) {
          refetchEvent();
        }

        if (isEventEditorV2Enabled) {
          navigate(
            generatePath(DASHBOARD_EVENT_EDITOR_REGISTRATION_ROUTE, {
              id: String(parentEventId!)
            })
          );
        } else {
          navigate(`/dashboard/event/data/${parentEventId}`);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const renderFeedbackHelpText = (): string | JSX.Element => {
    if (feedbackStatus === FEEDBACK_STATUS.SENT) {
      return (
        <div>
          {`Feedback form was sent on ${feedbackTime}. If you change the guests now, we suggest resending the form.`}
          <a
            href={'https://help.epochapp.com/articles/how-do-i-resend-a-feedback-form'}
            target={'_blank'}
            rel="noreferrer"
          >
            <span className="tw-mb-0 tw-ml-1 tw-text-xs tw-text-primary-grey tw-underline">
              {'Learn more'}
            </span>
          </a>
        </div>
      );
    }
    return `Feedback form will be sent on ${feedbackTime}`;
  };

  const renderSubHeaderText = (): React.ReactNode => {
    let descriptionText =
      'This number will replace attendance data for all guests. You won’t get any attendee information, like department and location. You can always remove the estimate to restore check-in data.';
    if (isThirtyDaysAfterEventEnded) {
      descriptionText =
        'Estimated attendance is now closed. Changes to estimated attendance can only be made up to 30 days after the event ends.';
    }
    if (isUsingEstimatedAttendees) {
      descriptionText =
        'This event is using estimated attendees to calculate attendance. To check in guests individually, remove the estimate.';
    }

    return (
      <div className="tw-flex tw-flex-col tw-gap-2">
        {loading ? (
          <Skeleton className="tw-h-4 tw-w-18" />
        ) : (
          <span className="tw-text-sm tw-font-medium tw-leading-4 tw-text-foreground">
            {sessionTitle}
          </span>
        )}
        <span>{descriptionText}</span>
      </div>
    );
  };

  const adjustedFeedbackGuestList = useMemo(() => {
    const list = { ...FEEDBACK_GUEST_LIST };

    /**
     * Commented out per: https://team-epoch.slack.com/archives/C02HNP7B9D1/p1753196391378959
     * We should check the capacity at session level.
     */
    // if (hasUnlimitedSpots) {
    //   const { Waitlisted, ...updatedList } = list;
    //   return updatedList;
    // }

    return list;
  }, [hasUnlimitedSpots]);

  const renderSessionFeedbackBanner = (): JSX.Element | null => {
    if (isThirtyDaysAfterEventEnded) return null;

    if (isParentEventFeedbackEnable && sessionHasAttendedGuest) {
      return (
        <AlertBanner maxWidth="800px" variant="grey" className="tw-mt-4 tw-text-left">
          <span className="tw-flex tw-items-center tw-gap-2.5">
            <div className="tw-w-[15px]">
              <InfoIcon color={DARK_BLACK} />
            </div>
            <div className="tw-flex-1 tw-text-left">
              Guests were marked as attended in some sessions. Feedback forms will go to attendees.
              <a
                href={'https://help.epochapp.com/articles/how-does-estimated-attendance-work'}
                target={'_blank'}
                rel="noreferrer"
              >
                <span className="tw-mb-0 tw-ml-1 tw-text-sm tw-text-dark-black tw-underline">
                  {'Learn more'}
                </span>
              </a>
            </div>
          </span>
        </AlertBanner>
      );
    } else if (isParentEventFeedbackEnable && showSelectedGuestsBanner) {
      return (
        <AlertBanner maxWidth="800px" variant="grey" className="tw-mt-4 tw-text-left">
          <span className="tw-flex tw-items-center tw-gap-2.5">
            <div className="tw-w-[15px]">
              <InfoIcon color={DARK_BLACK} />
            </div>
            <div className="tw-flex-1 tw-text-left">
              You already selected guests for this event series. If you select new guests, it will
              apply to all sessions.
              <a
                href={'https://help.epochapp.com/articles/how-does-estimated-attendance-work'}
                target={'_blank'}
                rel="noreferrer"
              >
                <span className="tw-mb-0 tw-ml-1 tw-text-sm tw-text-dark-black tw-underline">
                  {'Learn more'}
                </span>
              </a>
            </div>
          </span>
        </AlertBanner>
      );
    }
    return null;
  };

  return (
    <>
      <Modal
        isOpen={isRemoveEstimatedAttendanceModalOpen}
        onRequestClose={(): void => {
          setIsRemoveEstimatedAttendanceModalOpen(false);
        }}
        showCloseButton
        headerText="Are you sure?"
        showPrimaryAction
        primaryActionLabel={'Remove'}
        primaryActionRed
        handlePrimaryAction={(): void => {
          handleAttendanceChange({ type: ACTION_TYPE.REMOVE, eventId: sessionOrEventId });
        }}
        showSecondaryAction
        secondaryActionLabel="Cancel"
        handleSecondaryAction={(): void => {
          setIsRemoveEstimatedAttendanceModalOpen(false);
        }}
        maxWidth="452px"
        zIndex={2000}
      >
        <div className="tw-mb-6 tw-text-sm tw-text-dark-black">
          Estimated attendees will be removed from this event.
        </div>
        <div className="tw-text-sm tw-text-dark-black">
          You’ll be able to check in guests individually. Any previous check-in data will be
          restored.
        </div>
      </Modal>
      <Formik
        initialValues={{ estimatedAttendees, selectedGuestStatuses }}
        enableReinitialize
        onSubmit={(values, { setSubmitting }): void => {
          setSubmitting(true);
        }}
      >
        <Form>
          <Modal
            isOpen={isEstimatedAttendanceModalOpen}
            onRequestClose={(): void => {
              setIsEstimatedAttendanceModalOpen(false);
            }}
            showCloseButton
            headerText="Estimated attendees"
            subHeaderText={renderSubHeaderText() as string}
            showPrimaryAction={!isThirtyDaysAfterEventEnded}
            primaryActionDisabled={!estimatedAttendees}
            primaryActionLabel={isUsingEstimatedAttendees ? 'Update' : 'Save'}
            handlePrimaryAction={(): void => {
              handleAttendanceChange({ type: ACTION_TYPE.UPDATE, eventId: sessionOrEventId });
            }}
            showSecondaryAction={!!isUsingEstimatedAttendees && !isThirtyDaysAfterEventEnded}
            secondaryActionLabel="Remove"
            secondaryActionRed
            handleSecondaryAction={(): void => {
              setIsRemoveEstimatedAttendanceModalOpen(true);
            }}
            maxWidth="510px"
            marginBottom="0px"
          >
            <FormikInputTyped
              name="estimatedAttendees"
              label="Estimated attendees"
              type="number"
              min={1}
              onBlur={(e: React.ChangeEvent<HTMLInputElement>): void => {
                const numValue = Number(e.target.value);
                if (numValue < 1) {
                  setEstimatedAttendees(1);
                } else {
                  setEstimatedAttendees(numValue);
                }
              }}
              onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void => {
                const numValue = Number(value);
                setEstimatedAttendees(numValue < 1 ? 1 : numValue);
              }}
              $width="160px"
              hasBottomMargin={isParentEventFeedbackEnable}
              disabled={isThirtyDaysAfterEventEnded}
            />
            {isParentEventFeedbackEnable && (
              <FormikInputTyped
                name="selectedGuestStatuses"
                label="Send feedback forms to guests who are:"
                helpText={renderFeedbackHelpText()}
                size="full"
                hasBottomMargin={false}
                disabled={sessionHasAttendedGuest || isThirtyDaysAfterEventEnded}
              >
                {({ field }): JSX.Element => {
                  return (
                    <NewDropdown
                      items={adjustedFeedbackGuestList}
                      selectedValues={sessionHasAttendedGuest ? null : field.value}
                      onSelectedValuesChange={(selectedValues): void => {
                        setSelectedGuestStatuses(selectedValues);
                      }}
                      placeholder="Select"
                      isMultiSelect
                      disabled={sessionHasAttendedGuest || isThirtyDaysAfterEventEnded}
                    />
                  );
                }}
              </FormikInputTyped>
            )}
            {renderSessionFeedbackBanner()}
          </Modal>
        </Form>
      </Formik>
    </>
  );
};

export default EstimatedAttendanceModal;
